import type { AssistantItem, AssistantToolItem } from './groupTurns';

export interface TextSegment {
  kind: 'text';
  id: string;
  texts: string[];
}
export interface ToolSegment {
  kind: 'tool';
  id: string;
  item: AssistantToolItem;
}
export type Segment = TextSegment | ToolSegment;

/**
 * Walk the chronological assistant items and emit a render-segment per run:
 *   - consecutive text items coalesce into one TextSegment (one speech bubble)
 *   - each tool item becomes its own ToolSegment (one muted activity row)
 *
 * Segment ids are derived from a per-call counter so that, given the same
 * input items, the same segment positions get the same id across re-renders —
 * giving React stable keys without depending on array-index identity. Tool
 * segment ids include the underlying tool_use_id so concurrent identical-
 * counter renders (e.g., during reconciliation) still distinguish tool rows.
 *
 * Lives in its own module so the helpers can be unit-tested without coupling
 * to the component file (and without tripping react-refresh's one-export rule).
 */
export function toSegments(items: AssistantItem[]): Segment[] {
  const segments: Segment[] = [];
  let textCounter = 0;
  for (const item of items) {
    if (item.kind === 'text') {
      const last = segments[segments.length - 1];
      if (last && last.kind === 'text') {
        last.texts.push(item.text);
      } else {
        segments.push({ kind: 'text', id: `text-${textCounter++}`, texts: [item.text] });
      }
    } else {
      segments.push({ kind: 'tool', id: `tool-${item.id}`, item });
    }
  }
  return segments;
}

/**
 * The agent is "looping" (busy but not producing visible output) in two cases:
 *  1. No items yet — between send and the first SSE event.
 *  2. Last item is a resolved tool pill — the result has landed and the agent
 *     is deciding what to do next (more tools, or a text reply).
 *
 * A pending (no-result) tool pill already has its own spinner; a streaming
 * text block is its own visible activity. Both suppress the indicator so it
 * doesn't compete with what's actually on screen.
 */
export function isAgentLooping(items: AssistantItem[]): boolean {
  if (items.length === 0) return true;
  const last = items[items.length - 1];
  return last.kind === 'tool' && last.result !== undefined;
}
